import Immutable from 'immutable';

import UnitSettings from 'sharedreact/data/UnitSettings';
import WarningsSettings from 'sharedreact/data/WarningsSettings';

const Content: {
  language: string,
  theme: string,
  units: UnitSettings,
  warningsSettings: WarningsSettings,
  // dev settings
  enableDevSettings: boolean,
  shouldTranslateText: boolean,
  showDebugButtons: boolean,
  font: string,
  accentColor: string,
  uiSize: string,
  logLevel: string,
  logToConsole: boolean,
  logRawData: boolean,
  logActions: boolean,
  logEvents: boolean,
  logHeartBeatEvents: boolean,
  logServerCalls: boolean,
  showRecorder: boolean,
  showExtendedLoadHistory: boolean
} = {
  language: '',
  theme: '',
  units: new UnitSettings(),
  warningsSettings: new WarningsSettings(),
  // dev settings
  enableDevSettings: false,
  shouldTranslateText: true,
  showDebugButtons: false,
  font: 'default',
  accentColor: 'eskogreen',
  uiSize: 'normal',
  logLevel: 'info',
  logToConsole: false,
  logRawData: false,
  logActions: false,
  logEvents: false,
  logHeartBeatEvents: false,
  logServerCalls: false,
  showRecorder: false,
  showExtendedLoadHistory: false
};

class Settings extends Immutable.Record(Content) {
  static get LanguageOptions (): Immutable.List<string> {
    // ESnext class fields are just a stage 3 TC39 proposal right now
    // (see https://github.com/tc39/proposal-class-fields). In the
    // meantime, we'll use a static getter instead.
    return Immutable.List([
      'en',
      'fr',
      'de',
      'es',
      'it',
      'ja',
      'zh',
      'zh-TW',
      'ko'
    ]);
  }

  static get ThemeOptions (): Immutable.List<string> {
    return Immutable.List([
      'light',
      'dark'
    ]);
  }

  static get Fonts (): Immutable.List<string> {
    return Immutable.List([
      'default',
      'opensans',
      'roboto',
      'system',
      'barlow_old',
      'opensans_old',
      'roboto_old'
    ]);
  }

  static get AccentColors (): Immutable.List<string> {
    return Immutable.List([
      'eskogreen',
      'eskoapp_green',
      'eskoapp_blue',
      'eskoapp_violet',
      'eskoapp_orange',
      'eskoapp_grey',
      'macos_blue',
      'macos_purple',
      'macos_pink',
      'macos_red',
      'macos_orange',
      'macos_yellow',
      'macos_green',
      'macos_graphite'
    ]);
  }

  static get UISizes (): Immutable.List<string> {
    return Immutable.List([
      'normal',
      'small',
      'extrasmall'
    ]);
  }

  static get LogLevels (): Immutable.List<string> {
    return Immutable.List([
      'trace',
      'debug',
      'info',
      'warn',
      'error',
      'fatal'
    ]);
  }

  static getFontDisplayName (font: string | null) : string | null {
    switch (font) {
      case 'default':
        return 'Barlow (default)';
      case 'opensans':
        return 'Open Sans';
      case 'roboto':
        return 'Roboto';
      case 'system':
        return 'System';
      case 'barlow_old':
        return 'Barlow (no Chinese)';
      case 'opensans_old':
        return 'Open Sans (no Chinese)';
      case 'roboto_old':
        return 'Roboto (no Chinese)';
    }
    return font;
  }

  mergeSettings (newValues: {[key: string]: any} | null): Settings {
    let updatedSettings: Settings = this;

    for (const key in newValues) {
      if (Object.prototype.hasOwnProperty.call(newValues, key)) {
        if (key === 'language') {
          if (newValues['language'] != null && newValues['language'] !== '') {
            updatedSettings = updatedSettings.set('language', newValues['language']);
          }
        } else if (key === 'units') {
          if (newValues['units'] != null) {
            updatedSettings = updatedSettings.set('units', UnitSettings.fromJS(newValues['units']));
          }
        } else if (updatedSettings.has(key)) {
          if (key === 'warningsSettings') {
            updatedSettings = updatedSettings.set(key, new WarningsSettings(newValues[key]));
          } else {
            updatedSettings = updatedSettings.set(key, newValues[key]);
          }
        }
      }
    }

    return updatedSettings;
  }

  setEnableDevSettings (enableDevSettings: boolean): Settings {
    if (!enableDevSettings) {
      // if dev settings are not shown, the dev values show be set back to its default values
      return this.set('enableDevSettings', false)
        .set('font', 'default')
        .set('accentColor', 'eskogreen')
        .set('uiSize', 'normal')
        .set('shouldTranslateText', true)
        .set('showDebugButtons', false)
        .set('logLevel', 'info')
        .set('logToConsole', false)
        .set('logRawData', false)
        .set('logEvents', false)
        .set('logHeartBeatEvents', false)
        .set('logServerCalls', false)
        .set('showRecorder', false)
        .set('showExtendedLoadHistory', false);
    }
    return this.set('enableDevSettings', true);
  }
}

export default Settings;
